import { useEffect, useState } from 'react';
import { DATA_SET_PAGINATION_COUNT } from 'constants/dataSets';
import {
  useCreateStandardTopicMutation, useDeleteStandardTopicMutation,
  useGetStandardsListQuery, useGetStandardTopicsListQuery,
  useUpdateStandardTopicMutation,
} from 'store/slices/standards/apis/standardsApi';

import StandardsView from './StandardsView';

import type IStandardTopicsItem from 'store/slices/standards/interfaces/IStandardTopicsItem';

function Standards() {
  const [activeTab, setActiveTab] = useState(-1);
  const [topics, setTopics] = useState<IStandardTopicsItem[]>([]);
  const [offset, setOffset] = useState(0);
  const [openNewTopic, setOpenNewTopic] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<number>(-1);
  const [openEditField, setOpenEditField] = useState(false);
  const [openEditSubTopicField, setOpenEditSubTopicField] = useState(false);
  const [openKeywordsField, setOpenKeywordsField] = useState(false);
  const [isSubtopic, setIsSubtopic] = useState(false);
  const [text, setText] = useState('');

  const [createStandardTopic] = useCreateStandardTopicMutation();
  const [deleteStandardTopic] = useDeleteStandardTopicMutation();
  const [updateStandardTopic] = useUpdateStandardTopicMutation();

  const { data: standards, isFetching: isStandardsLoading } = useGetStandardsListQuery({
    offset: 0,
  });

  const { data: standardTopics, isFetching: isStandardTopicsLoading } = useGetStandardTopicsListQuery({
    offset,
    standardId: activeTab,
  });

  useEffect(() => {
    if (standards && standards?.data?.items?.length > 0) {
      setActiveTab(standards?.data?.items[0]?.id);
    }
  }, [standards]);

  const hasMore = standardTopics?.data.meta?.hasNext || false;

  const onLoadMore = () => {
    if (hasMore) {
      setOffset((prev) => prev + DATA_SET_PAGINATION_COUNT);
    }
  };

  useEffect(() => {
    if (standardTopics && standardTopics?.data?.items?.length > 0) {
      setTopics((prev) => {
        const existingIds = new Set(prev.map((topic) => topic.id));
        const newItems = standardTopics?.data?.items.filter((item) => !existingIds.has(item.id));
        return [...prev, ...newItems];
      });
    }
  }, [standardTopics]);

  const onClickTab = (tabId: number) => {
    setActiveTab(tabId);
    setOffset(0);
    setTopics([]);
  };

  const addTopic = () => {
    setOpenNewTopic(!openNewTopic);
  };

  const addSubTopic = (id: number) => {
    setSelectedItem(id);
    setOpenEditSubTopicField(true);
    setOpenEditField(false);
    setOpenKeywordsField(false);
    setText('');
  };

  const onOpenFieldKeyWords = (id: number) => {
    setSelectedItem(id);
    setOpenKeywordsField(true);
    setOpenEditField(false);
    setOpenEditSubTopicField(false);
    setText('');
  };

  const onCancel = () => {
    setOpenNewTopic(false);
    setSelectedItem(-1);
    setDeleteModalOpen(false);
    setOpenEditField(false);
    setOpenEditSubTopicField(false);
    setOpenKeywordsField(false);
    setIsSubtopic(false);
    setText('');
  };

  const onCreateNewTopic = (topicName: string) => {
    const newTopic = {
      title: topicName,
      depth: 0,
      standardId: activeTab,
      keywords: [],
      parentId: null,
    };
    setTopics([]);
    setOffset(0);
    createStandardTopic(newTopic);
    onCancel();
  };

  const onCreateNewSubtopic = () => {
    const newTopic = {
      title: text,
      depth: 1,
      standardId: null,
      keywords: [],
      parentId: selectedItem,
    };
    setTopics([]);
    setOffset(0);
    createStandardTopic(newTopic);
    onCancel();
  };

  const onDeleteItem = (id: number, subtopic?: boolean) => {
    setSelectedItem(id);
    setDeleteModalOpen(true);
    setIsSubtopic(subtopic || false);
  };

  const removeTopicRecursively = (topicsList: IStandardTopicsItem[], idToRemove: number): IStandardTopicsItem[] => (
    topicsList.filter((topic) => topic.id !== idToRemove).map((topic) => {
      if (topic?.children && topic?.children?.length > 0) {
        return {
          ...topic,
          children: topic?.children.filter((child) => child.id !== idToRemove),
        };
      }
      return topic;
    })
  );

  const onDeleteConfirm = () => {
    deleteStandardTopic({ id: selectedItem });
    setTopics((prev) => removeTopicRecursively(prev, selectedItem));
    onCancel();
  };

  const onEditTopicName = (id: number) => {
    setSelectedItem(id);
    setOpenEditField(true);
    setOpenEditSubTopicField(false);
    setOpenKeywordsField(false);
    const findTopic = topics.find((topic) => topic.id === id);
    setText(findTopic?.title || '');
  };

  const onEditSubtopicName = (id: number) => {
    setSelectedItem(id);
    setOpenEditSubTopicField(true);
    setOpenEditField(false);
    setOpenKeywordsField(false);
    const findTopic = topics.find((topic) => topic.id === id)
      || topics.find((topic) => topic.children?.some((child) => child.id === id))
        ?.children?.find((child) => child.id === id);
    setText(findTopic?.title || '');
  };

  const onChangeText = (value: string) => {
    setText(value);
  };

  const onSaveTopic = () => {
    const findTopic = topics.find((topic) => topic.id === selectedItem);
    const newTopic = {
      title: text,
      depth: findTopic?.depth || 0,
      standardId: activeTab,
      keywords: findTopic?.keywords || [],
      parentId: findTopic?.parentId || null,
    };

    if (findTopic?.title !== text) {
      updateStandardTopic({
        id: selectedItem,
        data: newTopic,
      }).then((response) => {
        if ('data' in response) {
          const topicItem = {
            ...newTopic,
            id: findTopic?.id,
            children: findTopic?.children || [],
          };
          setTopics((prev) => prev.map((topic) => (
            topic.id === selectedItem ? topicItem : topic)));
        }
      });
    }
    onCancel();
  };

  const onUpdateSubtopic = () => {
    const findTopic = topics.find((topic) => topic.id === selectedItem)
      || topics.find((topic) => topic.children?.some((child) => child.id === selectedItem))
        ?.children?.find((child) => child.id === selectedItem);
    const newTopic = {
      title: text,
      depth: findTopic?.depth || 0,
      standardId: findTopic?.standardId || null,
      keywords: findTopic?.keywords || [],
      parentId: findTopic?.parentId || null,
    };

    if (findTopic?.title !== text) {
      updateStandardTopic({
        id: selectedItem,
        data: newTopic,
      }).then((response) => {
        if ('data' in response) {
          const topicItem = {
            ...newTopic,
            id: findTopic?.id,
            children: [],
          };
          setTopics((prev) => prev.map((topic) => {
            if (topic.id === topicItem.parentId) {
              return {
                ...topic,
                children: topic.children?.map((child) => (
                  child.id === selectedItem ? topicItem : child)),
              };
            }
            return topic;
          }));
        }
      });
    }
    onCancel();
  };

  const onSaveKeyWords = () => {
    const findTopic = topics.find((topic) => topic.id === selectedItem)
      || topics.find((topic) => topic.children?.some((child) => child.id === selectedItem))
        ?.children?.find((child) => child.id === selectedItem);

    if (findTopic) {
      const newTopic = {
        title: findTopic?.title,
        depth: findTopic?.depth || 0,
        standardId: findTopic?.standard?.id || null,
        keywords: findTopic?.keywords ? [...findTopic.keywords, text] : [text],
        parentId: findTopic?.parentId || null,
      };

      if (findTopic?.title !== text) {
        updateStandardTopic({
          id: selectedItem,
          data: newTopic,
        }).then((response) => {
          if ('data' in response) {
            const topicItem = {
              ...newTopic,
              id: findTopic.id,
              children: findTopic?.children || [],
            };
            setTopics((prev) => prev.map((topic) => {
              if (topic.id === selectedItem) {
                return topicItem;
              }

              if (topic.children?.some((child) => child.id === selectedItem)) {
                return {
                  ...topic,
                  children: topic.children?.map((child) => (
                    child.id === selectedItem ? topicItem : child)),
                };
              }
              return topic;
            }));
          }
        });
      }
    }
    onCancel();
  };

  const onDeleteKeyword = (id: number, title: string) => {
    const findTopic = topics.find((topic) => topic.id === id)
      || topics.find((topic) => topic.children?.some((child) => child.id === id))
        ?.children?.find((child) => child.id === id);

    if (findTopic) {
      const newTopic = {
        title: findTopic?.title,
        depth: findTopic?.depth || 0,
        standardId: findTopic?.standard?.id || null,
        keywords: findTopic?.keywords ? findTopic.keywords.filter((keyword) => keyword !== title) : [],
        parentId: findTopic?.parentId || null,
      };

      if (findTopic?.title !== text) {
        updateStandardTopic({
          id,
          data: newTopic,
        }).then((response) => {
          if ('data' in response) {
            const topicItem = {
              ...newTopic,
              id: findTopic.id,
              children: findTopic?.children || [],
            };
            setTopics((prev) => prev.map((topic) => {
              if (topic.id === id) {
                return topicItem;
              }

              if (topic.children?.some((child) => child.id === id)) {
                return {
                  ...topic,
                  children: topic.children?.map((child) => (
                    child.id === id ? topicItem : child)),
                };
              }
              return topic;
            }));
          }
        });
      }
    }
    onCancel();
  };

  const loading = isStandardsLoading || isStandardTopicsLoading;

  return (
    <StandardsView
      loading={loading}
      addTopic={addTopic}
      standards={standards?.data.items}
      activeTab={activeTab}
      onClickTab={onClickTab}
      onLoadMore={onLoadMore}
      hasMore={hasMore}
      topics={topics}
      openNewTopic={openNewTopic}
      onCancel={onCancel}
      onCreateNewTopic={onCreateNewTopic}
      onDeleteItem={onDeleteItem}
      isDeleteModalOpen={isDeleteModalOpen}
      onDeleteConfirm={onDeleteConfirm}
      onEditTopicName={onEditTopicName}
      openEditField={openEditField}
      selectedItem={selectedItem}
      onChangeText={onChangeText}
      text={text}
      onSaveTopic={onSaveTopic}
      addSubTopic={addSubTopic}
      openEditSubTopicField={openEditSubTopicField}
      onCreateNewSubtopic={onCreateNewSubtopic}
      onEditSubtopicName={onEditSubtopicName}
      onUpdateSubtopic={onUpdateSubtopic}
      isSubtopic={isSubtopic}
      onOpenFieldKeyWords={onOpenFieldKeyWords}
      openKeywordsField={openKeywordsField}
      onSaveKeyWords={onSaveKeyWords}
      onDeleteKeyword={onDeleteKeyword}

    />
  );
}

export default Standards;
